<!-- The settings, in three groups instead of nine.
  --
  -- What was wrong with the old panel was not the number of controls but that
  -- its SHAPE changed: two orthogonal flags (batchMode, pictureElements)
  -- drove three different arrangements of one flat list, so there was nothing
  -- to learn. Width and "crop to a size" asked the same question with Quality
  -- sitting between them; Rotate was a loose row of buttons; the EXIF checkbox
  -- was a privacy control living among transform controls.
  --
  -- Grouped by what the control is FOR, the arrangement holds still. Only two
  -- things still differ between the paths, and both are named:
  --
  --   "Crop a region" exists only where you can point at an image, so single
  --   mode only. Everything else here is a parameter and applies to one image
  --   or ten identically -- that is the test for anything added later.
  --
  --   A batch makes one file per image, so it picks ONE format; a single image
  --   makes a responsive set, so it picks several. Same question, two shapes.
  --
  -- This component is shared by both paths on purpose. Two copies of Quality
  -- and Formats would drift, which is exactly what happened to the two picture
  -- snippet builders before they were merged.
  -->
<template>
    <div class="flex flex-col gap-5">

        <!-- On a task page the settings arrive already right for the job the
          -- URL names, so the panel starts folded. Folded, NOT hidden: what it
          -- is set to is stated in words, and one click opens the lot. A
          -- visitor who came for "png to webp" and then wants to resize as
          -- well should not have to go and find another site. -->
        <div v-if="configured" class="rounded-2xl border border-rule bg-sunk-soft p-5 flex flex-col gap-2">
            <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('options.configuredTitle') }}</span>
            <p class="text-[15px] text-ink-soft leading-snug">{{configured}}</p>
            <!-- data-panel-toggle so a suite can open the panel without
              -- matching its label. tests/video_to_gif_test.sh looked for the
              -- word "settings" and the button says "Show advanced options",
              -- so a case that needed a control inside the panel reported the
              -- control missing -- which reads as a feature that was never
              -- wired up. -->
            <button data-panel-toggle :data-panel-open="open ? 'yes' : 'no'"
                    @click.prevent="open = !open"
                    class="self-start text-[13px] font-bold text-brand underline underline-offset-2">
                {{ open ? t('options.hide') : t('options.show') }}
            </button>
        </div>

        <template v-if="open">

        <!-- GEOMETRY: everything that changes the shape or the size.
          --
          -- THE WHOLE CARD STANDS DOWN ON THE SHEET PAGE. The shape preset,
          -- the flip and the rotation went first, which left it holding one
          -- control -- and then that one went too, because measurement
          -- settled what it was for. Asked as a question: "does this only
          -- apply to the optimized images?"
          --
          -- Measured on four 1600x1067 photographs. At 100% the batch writes
          -- s_1_1280.jpg and the sheet comes out 710x484; at 50% it writes
          -- s_1_640.jpg and the sheet comes out 710x484. The SAME sheet,
          -- built from half-resolution sources. So the control does not
          -- resize the thing this page makes -- it can only degrade it, and
          -- at 25% a 320px source is being enlarged into a 400px cell.
          --
          -- Quality still covers "make the files in the zip smaller", which
          -- is the one job the width was being kept for. -->
        <div v-if="!sheetMode"
             class="rounded-2xl border border-rule bg-raised p-5 flex flex-col gap-4">
            <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('options.sizeShape') }}</span>

            <!-- THE CLIP'S OWN CONTROLS, and the only two that matter here.
              --
              -- These do not become directives. They decide what the browser
              -- encodes before anything is uploaded at all, which is why they
              -- are separate from the width select below: that one asks the
              -- CDN to resize what it is given, and by then the frames have
              -- already been drawn at whatever size these chose.
              --
              -- Frames a second is the cheapest saving there is: halving it
              -- halves the file with none of the loss of detail that halving
              -- the width costs. It is first for that reason.
              --
              -- 640 is the ceiling because a GIF of any length stops being
              -- sendable past it -- 1024x576 for 10 seconds is 31 MB before it
              -- leaves the browser, measured. -->
            <template v-if="videoMode">
                <div class="flex flex-col gap-1.5">
                    <label for="videoFps" class="text-[13px] text-ink-soft">{{ t('options.videoFps') }}</label>
                    <select class="select select-bordered select-sm rounded-lg w-full" id="videoFps"
                            data-video-fps
                            :value="uiStore.videoFps" @change="set('videoFps', $event.target.value)">
                        <option v-for="r in [5, 8, 10, 12, 15]" :key="r" :value="r">{{r}}</option>
                    </select>
                </div>
                <div class="flex flex-col gap-1.5">
                    <label for="videoWidth" class="text-[13px] text-ink-soft">{{ t('options.videoWidth') }}</label>
                    <select class="select select-bordered select-sm rounded-lg w-full" id="videoWidth"
                            data-video-width
                            :value="uiStore.videoWidth" @change="set('videoWidth', $event.target.value)">
                        <option v-for="px in [240, 320, 480, 640]" :key="px" :value="px">{{px}} px</option>
                    </select>
                </div>
            </template>

            <div v-if="!videoMode" class="flex flex-col gap-1.5">
                <label for="percentageScreen" class="text-[13px] text-ink-soft">{{ t('options.width') }}</label>
                <select class="select select-bordered select-sm rounded-lg w-full" id="percentageScreen"
                        :value="uiStore.pct" @change="set('pct', $event.target.value)">
                    <option value="100">{{ t('options.fullWidth') }}</option>
                    <option v-for="pctidx in uiStore.percentageList" :key="pctidx" :value="pctidx">{{pctidx}} %</option>
                </select>
            </div>

            <!-- w_/h_ + m_cropbox: a SHAPE, which is a parameter, so both paths
              -- get it. Cropping a particular region is not, and does not.
              --
              -- Withheld from a document. It works -- it is a directive like
              -- any other -- but cropping every page of a form into a square
              -- avatar frame is not something anybody sets out to do, and it
              -- sits directly above a flip that cannot work at all. -->
            <!-- Withheld from a clip as well as from a document. A preset
              -- emits w_/h_/m_cropbox, and the CDN discards a width alongside
              -- a crop -- so on an animation it fights the cr_ directive the
              -- crop tool now uses. And cropping every frame of a video into
              -- an avatar square is not something anybody sets out to do,
              -- which is the argument that took it off a document too. -->
            <div v-if="!documentMode && !videoMode" class="flex flex-col gap-1.5">
                <label for="presetSelect" class="text-[13px] text-ink-soft">{{ t('options.cropTo') }}</label>
                <select class="select select-bordered select-sm rounded-lg w-full" id="presetSelect"
                        :value="uiStore.preset === null ? '' : uiStore.preset"
                        @change="set('preset', $event.target.value === '' ? null : $event.target.value)">
                    <option value="">{{ t('options.keepShape') }}</option>
                    <option v-for="p in uiStore.presetList" :key="p.id" :value="p.id">{{ t('presets.' + p.id) }} &mdash; {{p.w}}&times;{{p.h}}</option>
                </select>
                <!-- Three states, because the crop lives in two places.
                  -- On a phone the stage panel is not rendered at all, so this
                  -- button is the ONLY way to reach the cropper and it stays.
                  -- On a desktop the stage offers cropping directly once the
                  -- image is ready, and a second trigger here -- a button in a
                  -- list of settings, opening a surface in the other panel --
                  -- was the confusing part. Before it is ready, say so instead
                  -- of offering a button that leads somewhere not yet there. -->
                <button v-if="mode === 'single' && cropTrigger === 'button'" @click.prevent="$emit('crop-region')"
                        class="btn btn-sm rounded-lg mt-1 self-start"
                        :class="uiStore.crop ? 'btn-primary' : 'btn-outline'">
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M6 2v14a2 2 0 0 0 2 2h14"></path><path d="M2 6h14a2 2 0 0 1 2 2v14"></path>
                    </svg>
                    {{ uiStore.crop ? t('crop.changeRegion') : t('crop.region') }}
                </button>
                <p v-else-if="mode === 'single' && cropTrigger === 'hint'"
                   class="mt-1 text-[13px] text-ink-mute">{{ t('crop.later') }}</p>
                <p v-else class="text-[13px] text-ink-label leading-snug">
                    {{ t('options.cropBatchNote') }}
                </p>
            </div>

            <!-- No ImageEngine directive behind these two: flip_h and
              -- mirror_true both answer 412, so the flip happens on a canvas
              -- before the upload. See src/lib/imageTransform.js. -->
            <!-- Hidden when the stage is showing the same two buttons on the
              -- picture itself. A batch has no stage, so there the panel is the
              -- only place flipping can live and this is where it stays. One
              -- concept, one control, one place -- per scenario. -->
            <div v-if="cropTrigger !== 'none' && !documentMode" class="flex flex-col gap-1.5">
                <span class="text-[13px] text-ink-soft">{{ t('options.flip') }}</span>
                <div class="flex gap-2">
                    <button @click.prevent="toggle('flipH')" class="btn btn-sm rounded-lg"
                            :class="uiStore.flipH ? 'btn-primary' : 'btn-outline'"
                            :aria-pressed="uiStore.flipH ? 'true' : 'false'">
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3"></path><path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3"></path>
                            <path d="M12 2v2M12 8v2M12 14v2M12 20v2"></path>
                        </svg>
                        {{ t('options.flipH') }}
                    </button>
                    <button @click.prevent="toggle('flipV')" class="btn btn-sm rounded-lg"
                            :class="uiStore.flipV ? 'btn-primary' : 'btn-outline'"
                            :aria-pressed="uiStore.flipV ? 'true' : 'false'">
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3"></path><path d="M21 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3"></path>
                            <path d="M2 12h2M8 12h2M14 12h2M20 12h2"></path>
                        </svg>
                        {{ t('options.flipV') }}
                    </button>
                </div>
            </div>

            <!-- r_<deg>, and NOT on a clip.
              --
              -- A photograph out of a camera can be sideways and rotating it
              -- is the only fix. A video carries its own display rotation and
              -- every player honours it, so a clip that plays upright decodes
              -- upright -- the browser draws it the right way round into the
              -- canvas before a frame is ever encoded. Offering four buttons
              -- to fix something already fixed is four ways to break it. -->
            <!-- ...AND NOT WHEN THE PICTURE ALREADY CARRIES THEM.
              -- The two quarter turns are in the tool row under the stage now,
              -- beside the crop and the flips, because that is where somebody
              -- straightening a photograph is looking. This row stays for the
              -- places that row is not rendered: a document (no crop surface,
              -- and a PDF built wrong is exactly what rotation rescues), a
              -- batch (no stage at all), and a meme (the whole tool row stands
              -- down there). Same bargain cropTrigger strikes for the cropper.
              --
              -- Absolute angles rather than steps, deliberately: with no
              -- picture beside them there is nothing to press "again" against,
              -- and in a list of settings a value is the readable thing. -->
            <div v-if="!videoMode && !stageTurns" class="flex flex-col gap-1.5">
                <span class="text-[13px] text-ink-soft">{{ t('options.rotate') }}</span>
                <div class="flex gap-2">
                    <button v-for="deg in [0, 90, 180, 270]" :key="deg" @click.prevent="set('rotate', deg)"
                            class="btn btn-sm rounded-lg"
                            :class="uiStore.rotate === deg ? 'btn-primary' : 'btn-outline'">{{deg}}&deg;</button>
                </div>
            </div>
        </div>

        <!-- WHAT COMES OUT: how hard it is squeezed, and into which formats.
          --
          -- THE WHOLE GROUP IS WITHHELD FROM A CLIP.
          --
          -- The format list because a page that exists to make a GIF offering
          -- five other formats is offering to defeat itself: choosing JPEG
          -- there hands back one still frame. tasks.json presets ["gif"], so
          -- there is nothing to choose.
          --
          -- The quality slider because the two controls above it already
          -- govern the size and do it better. It is not dead -- measured,
          -- cmpr_60 takes a 1.46 MB delivered GIF to 1.15 -- but on a
          -- 256-colour format it is a JPEG lever wearing the wrong label, and
          -- frames-a-second buys more for less. Withheld at the owner's
          -- direction, and the figure is recorded so it can come back. -->
        <div v-if="!videoMode"
             class="rounded-2xl border border-rule bg-raised p-5 flex flex-col gap-4">
            <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('options.qualityFormat') }}</span>

            <!-- THE SLIDER CARRIES QUALITY; THE CDN TAKES COMPRESSION.
              --
              -- `cmpr_0-99` is an ImageEngine directive meaning how hard to
              -- squeeze, so it runs the OPPOSITE way to the word above it.
              -- This control bound the thumb to `cmpr` and the number to
              -- `100 - cmpr`, so the two halves of one control ran in
              -- opposite directions: measured, thumb at 10 read "90%", thumb
              -- at 90 read "10%". Dragging a slider labelled Quality to the
              -- right made the picture worse.
              --
              -- Reported from a screenshot where the number said 88% and the
              -- thumb sat a tenth of the way along -- which is what 88%
              -- quality looks like when the track is really compression.
              --
              -- Both halves are the same expression now. The STORE is
              -- unchanged and still holds compression, because that is what
              -- buildDirectives() emits and what a task preset means
              -- (/compress-scanned-documents sets cmpr 25), so this fixes the
              -- control without changing a single delivered byte. -->
            <div class="flex flex-col gap-2">
                <div class="flex items-baseline justify-between">
                    <label for="qualityRange" class="text-[13px] text-ink-soft">{{ t('options.quality') }}</label>
                    <span class="text-sm tabular-nums text-ink-mute">{{ uiStore.cmpr === null ? t('options.automatic') : (100 - uiStore.cmpr) + '%' }}</span>
                </div>
                <input id="qualityRange" type="range" min="1" max="99" step="1" class="range range-primary range-xs"
                       :value="uiStore.cmpr === null ? 60 : (100 - uiStore.cmpr)"
                       @change="setQuality($event.target.value)" />
                <button v-show="uiStore.cmpr !== null" @click.prevent="setQuality(null)"
                        class="self-start text-xs font-bold text-brand underline underline-offset-2">{{ t('options.backToAuto') }}</button>
            </div>

            <!-- The developer's half of a batch. Off, a batch is one smaller
              -- file per image. On, it is the whole responsive set per image
              -- plus a <picture> element for each, which costs several
              -- transforms per image instead of one. -->
            <!-- The <picture> element checkbox used to live here.
              -- It was on for one task and explicitly off for twelve, so twelve
              -- registry entries existed to say "not me", and the panel carried
              -- a control almost nobody wanted. The capability is unchanged and
              -- still generates the snippet, the alt column and the fallback
              -- row -- it is simply reached by choosing the task that is about
              -- it, /responsive-image-generator, which presets it. On demand,
              -- rather than always present and usually off. -->

            <!-- One format, or several: the same question in the shape each
              -- path needs it. -->
            <div v-if="picksOneFormat" class="flex flex-col gap-1.5">
                <div class="flex items-baseline justify-between">
                    <span class="text-[13px] text-ink-soft">{{ t('options.outputFormat') }}</span>
                    <span class="text-sm tabular-nums text-ink-mute">{{ t('options.onePerImage') }}</span>
                </div>
                <select class="select select-bordered select-sm rounded-lg w-full"
                        :value="uiStore.batchFormat"
                        @change="set('batchFormat', $event.target.value)"
                        :aria-label="t('options.outputFormatAria')">
                    <option v-for="f in uiStore.formatCatalog" :key="f.id" :value="f.id">{{f.label}} &mdash; {{ t('formats.' + f.id) }}</option>
                </select>
            </div>

            <!-- f_<format>. Fewer formats means fewer requests per
              -- optimization, so this reduces work rather than adding it. -->
            <div v-else class="flex flex-col gap-2">
                <div class="flex items-baseline justify-between">
                    <span class="text-[13px] text-ink-soft">{{ t('options.formats') }}</span>
                    <span class="text-sm tabular-nums text-ink-mute">{{formatCount}}</span>
                </div>
                <div class="flex flex-col gap-1.5">
                    <label v-for="f in uiStore.formatCatalog" :key="f.id" class="flex items-start gap-2.5 cursor-pointer">
                        <input type="checkbox" class="checkbox checkbox-primary checkbox-xs mt-1"
                               :data-format="f.id"
                               :checked="uiStore.enabledFormats.includes(f.id)"
                               @change="setFormat(f.id, $event.target.checked)" />
                        <span class="leading-snug">
                            <span class="text-[15px] font-bold">{{f.label}}</span>
                            <span class="block text-[13px] text-ink-label">{{ t('formats.' + f.id) }}</span>
                        </span>
                    </label>
                </div>
            </div>
        </div>

        <!-- meta_true. Stripping is the default, which is worth saying.
          --
          -- NOT ON A DOCUMENT, and this one is worse than a dead control.
          -- Pages are rasterised on the server with PyMuPDF, which writes a
          -- pixmap: measured, the JPEG carries JFIF, quantisation tables, a
          -- frame and Huffman tables, and no APP1 segment at all -- there is
          -- nowhere for camera or GPS data to be, whichever way the box is
          -- ticked. Offering it implies a scanned form might be carrying
          -- coordinates, which is a worry about something that cannot happen,
          -- and a reassurance nobody needed is still a question raised. -->
        <!-- Withheld from a clip for exactly the reason it is withheld from a
          -- document: there is nowhere for the data to be. Frames come off a
          -- CANVAS and gifenc writes no metadata block at all, so the switch
          -- cannot keep anything -- and leaving it on screen implies a holiday
          -- video might be carrying coordinates. A reassurance nobody needed
          -- is still a question raised. -->
        <div v-if="!documentMode && !videoMode && !sheetMode"
             class="rounded-2xl border border-rule bg-raised p-5 flex flex-col gap-4">
            <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('options.privacy') }}</span>
            <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" class="checkbox checkbox-sm mt-0.5"
                       :checked="uiStore.keepExif" @change="set('keepExif', $event.target.checked)" />
                <span>
                    <span class="block text-[15px] font-bold">{{ t('options.keepExif') }}</span>
                    <span class="block text-[13px] text-ink-label leading-snug">{{ t('options.keepExifNote') }}</span>
                </span>
            </label>
        </div>
        </template>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUiElements } from "../store/uiElements"
// The same ceiling the meme captions use, because it is the same renderer.
import { MAX_MEME_CHARS } from "../lib/meme"

const props = defineProps({
    // 'button' -> this is the only way in (a phone). 'hint' -> the stage will
    // offer it once the image is ready. 'none' -> the stage is offering it now.
    cropTrigger: { type: String, default: 'button' },
    // 'single' can point at the image; 'batch' can only set parameters
    mode: {type: String, default: 'single'},
    isMobilePhone: {type: Boolean, default: false},
    /* Looking at a DOCUMENT rather than a picture.
     *
     * Two of the controls below cannot reach a PDF's pages and one of them
     * never could: the flip and the region crop happen on a canvas before the
     * upload (see the note on `rotate` in the store -- flip_h and mirror_true
     * both answer 412), and a document is rasterised on the server, so
     * applyTransforms() is never in its path. A visitor pressing LEFT/RIGHT on
     * a scanned form got nothing at all, silently.
     *
     * The crop-to-shape select is a real directive and does work, but cropping
     * every page of a document into an avatar square is not a thing anybody
     * means to do, and it is the control that most invites the question.
     *
     * Width, rotate, quality, output format and the metadata switch all reach
     * a page and all stay. */
    documentMode: {type: Boolean, default: false},
    /* A PAGE WHOSE OUTPUT IS ONE COMPOSED PICTURE.
     *
     * Same device as documentMode, and the same argument: four of these
     * controls apply ONE transform to the whole set, which on a page for
     * arranging a set is either meaningless or actively fighting the layout.
     * Reported as clutter, with the two cards crossed out on a screenshot.
     *
     * Hidden: the shape preset (a second, competing way to make everything
     * square, which then fights the "squares, cropped" layout), flip and
     * rotate (turning all fourteen photographs is not a thing anybody sets
     * out to do), and the EXIF switch -- the composed picture carries no
     * metadata at all, so leaving it on screen implies the sheet might.
     *
     * KEPT: the width, the quality and the format. Those decide the per-image
     * files in the zip, which is a real second output of this page and the
     * source of the saving it prints in large type. */
    sheetMode: {type: Boolean, default: false},
    /* The stage is showing the two quarter turns in its own tool row, so this
     * panel must not show a second set. See the note beside the row itself. */
    stageTurns: {type: Boolean, default: false},
    // The clip's own controls, on a page that takes a video. They act on
    // the conversion rather than on a CDN directive, so they cannot go in
    // the geometry block with pct -- see the note beside them.
    videoMode: {type: Boolean, default: false},
    // A one-line description of what a task page has already decided. Empty on
    // the homepage, where there is nothing to have decided.
    configured: {type: String, default: ''},
})
const emit = defineEmits(['change', 'crop-region'])
const uiStore = useUiElements()
const { t } = useI18n()

// Only where the task asked for it: a page that presets an opacity is a page
// the control belongs on.

// folded on a task page, open everywhere else
// A task page arrives collapsed, because its settings are already right and
// the "already set up for this" note says so. The exception is a page whose
// whole subject is one of these controls: hiding the scrim behind "Show
// advanced options" on /darken-image hides the reason the page exists.
// A task page arrives collapsed: its settings are already right and the
// "already set up for this" note says so. The exception is a page whose own
// control lives elsewhere -- /darken-image puts its scrim under the preview,
// and the panel underneath is the ordinary quality-and-format one, so it can
// stay shut like every other task's.
const open = ref(!props.configured)

/* Folded on arrival, open once there is something to adjust.
 *
 * A task page arrives with its settings already right, so on the way in the
 * panel is noise -- that part still holds. What did not hold is keeping it shut
 * AFTERWARDS: the card above says "already set up for this", which reads as
 * nothing to do here, while the two controls anybody actually wants on
 * /pdf-to-jpg -- the output format and the quality -- sit behind a link called
 * "Show the settings".
 *
 * That was defensible while a conversion took a button press and a wait. It is
 * not now that a document starts by itself: the pages land, the next thought is
 * "PNG please" or "smaller", and answering it meant going hunting.
 *
 * ONCE, and never again. Somebody who folds it back has said what they want,
 * and a panel that reopens itself on every re-run is a panel arguing with the
 * person using it.
 */
const hasResults = computed(() => uiStore.showDownloadables
    || (uiStore.batchItems.length > 0 && !uiStore.batchRunning)
    /* A CLIP counts as a result. /mp4-to-gif converts by itself the moment the
     * trimmer is accepted, and then the whole page is about the four controls
     * in here -- frames a second, the words, the width. "Already set up for
     * this" over a folded panel reads as nothing to do, which is exactly the
     * complaint /pdf-to-jpg produced and the same answer. */
    || (props.videoMode && uiStore.files.length > 0))

let unfoldedOnce = false
watch(hasResults, (now) => {
    if (now && !unfoldedOnce) {
        unfoldedOnce = true
        open.value = true
    }
})

// A batch without picture elements produces one file each, so it picks a
// single format. Everything else picks a set.
const picksOneFormat = computed(() => props.mode === 'batch' && !uiStore.pictureElements)

const formatCount = computed(() => props.mode === 'batch'
    ? t('options.filesEach', { n: uiStore.enabledFormats.length * 2 })
    : t('options.versions', uiStore.imageVersions.length))

// Every control funnels through here so there is exactly one place that
// decides a change is worth re-running the optimization for.
function set(key, value) {
    uiStore[key] = key === 'pct' ? Number(value) : value
    emit('change')
}

function toggle(key) {
    uiStore[key] = !uiStore[key]
    emit('change')
}

/* Takes QUALITY, which is what the control is called and what the slider now
 * carries. The store keeps COMPRESSION, so the inversion happens here -- once,
 * at the boundary, rather than in the template where it had drifted away from
 * the label beside it. */
function setQuality(value) {
    uiStore.cmpr = value === null ? null : 100 - parseInt(value, 10)
    emit('change')
}

function setFormat(formatId, enabled) {
    // rebuilds imageVersions, so the count, the table and the picture element
    // all follow from the one call
    uiStore.setFormatEnabled(formatId, enabled)
    emit('change')
}

function setPictureElements(on) {
    // the store also switches AVIF and WebP on, since a <picture> element
    // whose only source is a JPEG is an elaborate way of writing <img>
    uiStore.setPictureElements(on)
    emit('change')
}
</script>
